import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from "react";
import { createTranslator, locales, type Locale, type MessageKey } from "@gioia365/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { meQuery } from "../api/queries";

const STORAGE_KEY = "gioia365.locale";

export type Translate = (key: MessageKey, params?: Record<string, string | number>) => string;

type LocaleContextValue = {
  locale: Locale;
  /**
   * La scelta fatta su questo dispositivo (selettore delle pagine pubbliche senza una sessione): si ricorda.
   * Con una sessione il selettore salva invece la lingua della persona (`PublicShell`, `useUpdateMe`).
   */
  setLocale: (locale: Locale) => void;
  /**
   * La lingua scelta dalla persona (`preferredLocale`), senza ricordarla sul dispositivo; `null` torna alla
   * scelta del dispositivo.
   */
  applyLocale: (locale: Locale | null) => void;
  t: Translate;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

function readStoredLocale(): Locale | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return locales.find((candidate) => candidate === value) ?? null;
  } catch {
    return null;
  }
}

/** La lingua del browser, per chi non ha ancora scelto: inglese se è inglese, altrimenti italiano. */
function browserLocale(): Locale {
  return window.navigator.language.toLowerCase().startsWith("en") ? "en" : "it";
}

/**
 * Lingua dell'interfaccia (decisione del product owner sulla questione 39, 30 settembre 2026): la scelta della
 * persona (`preferredLocale`, applicata da `LocaleSync`), altrimenti quella del dispositivo (ricordata dal
 * selettore delle pagine pubbliche), altrimenti la lingua del browser. La lingua dell'azienda non conta.
 */
export function LocaleProvider({ children, initialLocale }: { children: ReactNode; initialLocale?: Locale }) {
  const [deviceLocale, setDeviceLocale] = useState<Locale>(() => initialLocale ?? readStoredLocale() ?? browserLocale());
  const [appliedLocale, setAppliedLocale] = useState<Locale | null>(null);
  const locale = appliedLocale ?? deviceLocale;
  // Sincronizza <html lang> anche al primo rendering, non solo dopo un cambio esplicito:
  // un utente che torna con "en" salvato deve trovare la pagina dichiarata in inglese.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const setLocale = useCallback((next: Locale) => {
    setDeviceLocale(next);
    // Una scelta del dispositivo vale subito, anche sopra una lingua della persona ancora applicata (per
    // esempio se il salvataggio della sua scelta non è riuscito), finché quella non cambia.
    setAppliedLocale(null);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable: language stays for this session */
    }
  }, []);
  const applyLocale = useCallback((next: Locale | null) => setAppliedLocale(next), []);
  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, applyLocale, t: createTranslator(locale) }),
    [locale, setLocale, applyLocale],
  );
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) throw new Error("useLocale must be used inside LocaleProvider");
  return context;
}

export function useT() {
  return useLocale().t;
}

/**
 * Applica la scelta della persona (`me.preferredLocale`); senza, vale quella del dispositivo. Legge soltanto
 * ciò che le guardie hanno già messo nella cache: non fa richieste. Sta nella radice del router, dentro
 * `LocaleProvider`. Il cambio di azienda non tocca `["me"]`, quindi non fa mai lampeggiare un'altra lingua.
 */
export function LocaleSync() {
  const { applyLocale } = useLocale();
  const queryClient = useQueryClient();
  const subscribe = useCallback((notify: () => void) => queryClient.getQueryCache().subscribe(notify), [queryClient]);
  const personal = useSyncExternalStore(subscribe, (): Locale | null => queryClient.getQueryData(meQuery.queryKey)?.preferredLocale ?? null);
  useEffect(() => {
    applyLocale(personal);
  }, [personal, applyLocale]);
  return null;
}
